import Layout from '@/components/Layout/Layout';
import AddRefundReason from './AddRefundReason';
import { useEffect, useMemo, useState } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { IRefundReason } from '@/interfaces/refundReason.interface';
import { formatDateTime, showToastNotification } from '@/utility/helper';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import { callApi, ThrowError } from '@/api/callApi';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { ActionConfig } from '@/interfaces/common.interface';
import PageHeading from '@/components/common/PageHeading';
import Card from '@/components/UI/Card';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';

interface StatesProp {
  showAddModel: boolean;
  reasonsList: IRefundReason[];
  isLoading: boolean;
  showDeleteModel: boolean;
  selectedReason: IRefundReason | null;
}

const RefundReason = () => {
  const { canView, canEdit, canDelete } = usePermissions();
  const [states, setStates] = useState<StatesProp>({
    showAddModel: false,
    reasonsList: [],
    isLoading: false,
    showDeleteModel: false,
    selectedReason: null,
  });

  const handleChangeState = (newState: Partial<StatesProp>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const fetchReasons = async () => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.refund}/get/reason`;
      const res = await callApi<IRefundReason[]>(PATH, { method: 'GET' });
      handleChangeState({
        reasonsList: res,
        isLoading: false,
      });
    } catch (error) {
      ThrowError(error);
      handleChangeState({ isLoading: false });
    }
  };

  const handleDeleteReason = async (id: string) => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.refund}/reason/${id}`;
      await callApi(PATH, { method: 'DELETE' });
      fetchReasons();
      showToastNotification('success', 'Variant deleted successfully');
    } catch (error: unknown) {
      ThrowError(error);
    } finally {
      handleChangeState({ isLoading: false });
    }
  };

  const columns: TableColumn<IRefundReason>[] = [
    {
      key: 'reason',
      header: 'Reason',
    },
    {
      key: 'createdAt',
      header: 'Created At',
      render: (item) => {
        return <span>{formatDateTime(item.createdAt, 'YYMMMDDHH')}</span>;
      },
    },
    {
      key: 'updatedAt',
      header: 'Last Update At',
      render: (item) => {
        return <span>{formatDateTime(item.updatedAt, 'YYMMMDDHH')}</span>;
      },
    },
  ];

  useEffect(() => {
    fetchReasons();
  }, []);

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.reasonsList);

  const reasonActionConfig: Record<string, ActionConfig<IRefundReason>> = {
    // view: {
    //     text: 'View',
    //     onClick: (variant) => { },
    //     permission: () => canView('product'),
    // },
    eidt: {
      text: 'Edit',
      onClick: (reason) => {
        handleChangeState({ showAddModel: true, selectedReason: reason });
      },
      permission: () => canEdit('order'),
    },
    delete: {
      text: 'Delete',
      onClick: (reason) => {
        handleChangeState({ showDeleteModel: true, selectedReason: reason });
      },
      permission: () => canDelete('order'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(reasonActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canView, canEdit, canDelete],
  );

  return (
    <Layout>
      <PageHeading
        text="Refund Reasons"
        button={{
          text: 'Add Variant',
          onClick: () => handleChangeState({ showAddModel: true }),
        }}
      />
      <Card>
        <CommonTable
          columns={columns}
          data={states.reasonsList}
          showCheckbox={false}
          getItemId={(item) => item.id}
          emptyMessage="No products found"
          loading={states.isLoading}
          minHeight="min-h-44"
          actions={actions}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
        />
      </Card>
      {states.showAddModel && (
        <AddRefundReason
          show={true}
          onSuccess={() => fetchReasons()}
          modelType={states.selectedReason?.id ? 'update' : 'add'}
          reasonText={states.selectedReason?.reason}
          reasonId={states.selectedReason?.id}
          onClose={() =>
            handleChangeState({ showAddModel: false, selectedReason: null })
          }
        />
      )}
      {states.showDeleteModel && (
        <ConfirmationModel
          show={true}
          onClose={() => {
            handleChangeState({ showDeleteModel: false, selectedReason: null });
          }}
          type="reason"
          onConfirm={() =>
            handleDeleteReason(states.selectedReason?.id as string)
          }
        />
      )}
    </Layout>
  );
};

export default RefundReason;
